/**
 * Password Change Component
 * Handles password updates for users
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import API_URL from '../../config/api';

const Container = styled.div``;

const Card = styled.div`
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
  color: var(--accent-primary);
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: var(--text-primary);
  opacity: 0.7;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &::placeholder {
    color: var(--text-primary);
    opacity: 0.4;
  }
`;

const PasswordStrength = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  background: ${props => {
    if (props.strength === 'strong') return 'rgba(76, 175, 80, 0.1)';
    if (props.strength === 'medium') return 'rgba(255, 152, 0, 0.1)';
    return 'rgba(244, 67, 54, 0.1)';
  }};
  color: ${props => {
    if (props.strength === 'strong') return '#4CAF50';
    if (props.strength === 'medium') return '#FF9800';
    return '#F44336';
  }};
`;

const Requirements = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 4px;
  font-size: 0.85rem;
`;

const RequirementsList = styled.ul`
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  line-height: 1.8;
`;

const Requirement = styled.li`
  color: ${props => props.met ? '#4CAF50' : 'var(--text-primary)'};
  opacity: ${props => props.met ? 1 : 0.6};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-secondary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: var(--text-primary);
  border: 2px solid var(--border-color);

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--accent-primary);
    transform: translateY(-2px);
  }
`;

const InfoBox = styled.div`
  background: rgba(255, 152, 0, 0.1);
  border-left: 4px solid #FF9800;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.6;
`;

function PasswordChange({ user }) {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const hasPassword = user?.hasPassword;

  const calculatePasswordStrength = (password) => {
    if (!password) return null;
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: 'weak', text: 'Weak Password' };
    if (strength <= 4) return { level: 'medium', text: 'Medium Strength' };
    return { level: 'strong', text: 'Strong Password' };
  };

  const passwordStrength = calculatePasswordStrength(formData.newPassword);

  const requirements = {
    length: formData.newPassword.length >= 8,
    uppercase: /[A-Z]/.test(formData.newPassword),
    lowercase: /[a-z]/.test(formData.newPassword),
    number: /[0-9]/.test(formData.newPassword),
    special: /[^a-zA-Z0-9]/.test(formData.newPassword)
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (hasPassword && !formData.currentPassword) {
      toast.error('Current password is required');
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordStrength.level === 'weak') {
      toast.error('Please choose a stronger password');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/api/auth/change-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      toast.success('Password changed successfully!');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  if (!hasPassword) {
    return (
      <Container>
        <Card>
          <InfoBox>
            Your account uses Google Sign-In and doesn't have a password. If you'd like to
            set up a password for direct login, please contact support.
          </InfoBox>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Change Password</Title>
        <Description>
          Update your password to keep your account secure. Choose a strong, unique password
          that you don't use anywhere else.
        </Description>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              type="password"
              id="currentPassword"
              name="currentPassword"
              placeholder="Enter your current password"
              value={formData.currentPassword}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              type="password"
              id="newPassword"
              name="newPassword"
              placeholder="Enter your new password"
              value={formData.newPassword}
              onChange={handleChange}
              required
            />
            
            {formData.newPassword && (
              <PasswordStrength strength={passwordStrength.level}>
                {passwordStrength.text}
              </PasswordStrength>
            )}

            <Requirements>
              <strong>Password must contain:</strong>
              <RequirementsList>
                <Requirement met={requirements.length}>
                  At least 8 characters
                </Requirement>
                <Requirement met={requirements.uppercase}>
                  One uppercase letter (A-Z)
                </Requirement>
                <Requirement met={requirements.lowercase}>
                  One lowercase letter (a-z)
                </Requirement>
                <Requirement met={requirements.number}>
                  One number (0-9)
                </Requirement>
                <Requirement met={requirements.special}>
                  One special character (!@#$%^&*)
                </Requirement>
              </RequirementsList>
            </Requirements>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Re-enter your new password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <PasswordStrength strength="weak">
                Passwords do not match
              </PasswordStrength>
            )}
          </FormGroup>

          <ButtonGroup>
            <CancelButton type="button" onClick={handleCancel} disabled={loading}>
              Cancel
            </CancelButton>
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </ButtonGroup>
        </Form>
      </Card>
    </Container>
  );
}

export default PasswordChange;
